import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api, query } from '../../api/client'
import type { AppSummary } from '../../api/types'
import { useDebounced } from '../../hooks/useDebounced'
import { SERIES_COLORS } from '../../lib/rank-series'
import { AppIcon } from '../AppCell'

export const MAX_COMPARE = 8

interface Props {
	apps: AppSummary[]
	onAdd(app: AppSummary): void
	onRemove(id: number): void
}

/** Chips for the selected apps plus a search field to add more. */
export function AppPicker({ apps, onAdd, onRemove }: Props) {
	const [q, setQ] = useState('')
	const term = useDebounced(q.trim())
	const results = useQuery({
		queryKey: ['search', term],
		queryFn: () => api.get<AppSummary[]>(`/api/v1/apps/search${query({ q: term, limit: 8 })}`),
		enabled: term.length > 1,
	})
	const full = apps.length >= MAX_COMPARE
	return (
		<div className="stack" style={{ gap: '0.5rem' }}>
			<div className="row">
				{apps.map((a, i) => (
					<span
						key={a.id}
						className="row card"
						style={{ padding: '0.25rem 0.5rem', gap: '0.4rem', borderColor: SERIES_COLORS[i % SERIES_COLORS.length] }}
					>
						<AppIcon url={a.iconUrl} />
						<span className="small">{a.name}</span>
						<button
							type="button"
							aria-label={`Remove ${a.name}`}
							onClick={() => onRemove(a.id)}
							style={{ padding: '0 0.4rem' }}
						>
							×
						</button>
					</span>
				))}
				<div style={{ position: 'relative', flex: '1 1 220px' }}>
					<input
						type="search"
						aria-label="Add app"
						placeholder={full ? `Up to ${MAX_COMPARE} apps` : 'Add an app…'}
						disabled={full}
						value={q}
						onChange={(e) => setQ(e.target.value)}
						style={{ width: '100%' }}
					/>
					{term.length > 1 && results.data && results.data.length > 0 && (
						<ul
							className="card"
							role="listbox"
							style={{
								position: 'absolute',
								zIndex: 5,
								left: 0,
								right: 0,
								margin: '0.25rem 0 0',
								padding: '0.25rem',
								listStyle: 'none',
							}}
						>
							{results.data
								.filter((r) => !apps.some((a) => a.id === r.id))
								.map((r) => (
									<li key={r.id}>
										<button
											type="button"
											role="option"
											aria-selected={false}
											className="app-cell"
											style={{ width: '100%', border: 0, textAlign: 'left' }}
											onClick={() => {
												onAdd(r)
												setQ('')
											}}
										>
											<AppIcon url={r.iconUrl} />
											<span className="small">
												{r.name} <span className="muted">· {r.developerName}</span>
											</span>
										</button>
									</li>
								))}
						</ul>
					)}
				</div>
			</div>
		</div>
	)
}
